import type {
  CreateManagementRoleParams,
  DeleteManagementRoleParams,
  GetRolesParams,
  ListResponseDto,
  ManagementRole,
  ResponseDto,
  UpdateManagementRoleParams,
} from "@/apps/admin/api/authTypes";
import {
  parseManagementRole,
  parseManagementRoleListResponse,
  parseResponse,
} from "@/apps/admin/api/authValidation";
import api, { TAG_TYPES } from "@/state/api";
import { providesTagsList } from "@/state/utilities/rtkToolkit";

const API_ROUTE = "auth/api/v1/roles";

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getManagementRoles: build.query<
      ListResponseDto<ManagementRole>,
      GetRolesParams
    >({
      query: () => API_ROUTE,
      transformResponse: parseManagementRoleListResponse,
      // A 403 means "this user has no admin access", which the UI shows on its own page.
      extraOptions: { notify: "unless403" },
      providesTags: providesTagsList(TAG_TYPES.MANAGEMENT_ROLE, {
        detail: true,
      }),
    }),
    createManagementRole: build.mutation<
      ResponseDto<ManagementRole>,
      CreateManagementRoleParams
    >({
      query: ({ role }) => ({
        url: API_ROUTE,
        method: "POST",
        body: role,
      }),
      transformResponse: (response: unknown) =>
        parseResponse(response, parseManagementRole),
      invalidatesTags: providesTagsList(TAG_TYPES.MANAGEMENT_ROLE, {
        detail: true,
      }),
    }),
    updateManagementRole: build.mutation<
      ResponseDto<ManagementRole>,
      UpdateManagementRoleParams
    >({
      query: ({ role, roleId }) => ({
        url: `${API_ROUTE}/${roleId}`,
        method: "PUT",
        body: role,
      }),
      transformResponse: (response: unknown) =>
        parseResponse(response, parseManagementRole),
      invalidatesTags: providesTagsList(TAG_TYPES.MANAGEMENT_ROLE, {
        detail: true,
      }),
    }),
    deleteManagementRole: build.mutation<void, DeleteManagementRoleParams>({
      query: ({ roleId }) => ({
        url: `${API_ROUTE}/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: providesTagsList(TAG_TYPES.MANAGEMENT_ROLE, {
        detail: true,
      }),
    }),
  }),
});

export const {
  useGetManagementRolesQuery,
  useCreateManagementRoleMutation,
  useUpdateManagementRoleMutation,
  useDeleteManagementRoleMutation,
} = extendedApi;

export { extendedApi as managementRolesApi };
